import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ChevronDown, ChevronRight, CreditCard, ExternalLink, MessageSquare, Package, Phone, RotateCcw,
  Search, ShieldAlert, ShoppingBag, Star, Truck, Wallet,
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import V2Shell from './V2Shell';

// Customer timeline: one customer's whole story in date order (CS team, 2026-10-16).
const API = 'api/replai/v2/customers/';
const KIND = {
  order: { label: 'Orders', icon: ShoppingBag, cls: 'text-[var(--ink)] bg-[var(--line-2)]' },
  shipped: { label: 'Shipped', icon: Truck, cls: 'text-green-800 bg-green-100' },
  delay: { label: 'Delays', icon: AlertTriangle, cls: 'text-amber-800 bg-amber-100' },
  ticket: { label: 'Tickets', icon: MessageSquare, cls: 'text-[var(--ask)] bg-[var(--ask-tint)]' },
  call: { label: 'Calls', icon: Phone, cls: 'text-[var(--ask)] bg-[var(--ask-tint)]' },
  claim: { label: 'Claims', icon: ShieldAlert, cls: 'text-red-800 bg-red-100' },
  return: { label: 'Returns', icon: RotateCcw, cls: 'text-purple-800 bg-purple-100' },
  refund: { label: 'Refunds', icon: Wallet, cls: 'text-green-800 bg-green-100' },
  dispute: { label: 'Disputes', icon: CreditCard, cls: 'text-red-800 bg-red-100' },
  review: { label: 'Reviews', icon: Star, cls: 'text-[#6e5214] bg-[var(--glow)]' },
};
const LINK = { ticket: 'Ticket', odoo: 'Odoo', shopify: 'Shopify', claim: 'Claimy', tracking: 'Tracking', case: 'Cases' };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const money = (v, c) => `${Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} ${c || 'EUR'}`;

const Event = ({ e }) => {
  const [open, setOpen] = useState(false);
  const k = KIND[e.kind] || KIND.order;
  const Icon = k.icon;
  const more = (e.extra?.lines || []).length || (e.extra?.messages || []).length;
  return (
    <li className="relative pl-14">
      <span className={`absolute left-0 top-1 flex h-9 w-9 items-center justify-center rounded-full ${k.cls}`}><Icon size={17} /></span>
      <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-3.5">
        <button type="button" onClick={() => more && setOpen(!open)} className={`flex w-full items-start gap-2 text-left ${more ? '' : 'cursor-default'}`}>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{e.title}</span>
              {e.status && <span className="rounded-full bg-[var(--line-2)] px-2 py-0.5 text-xs text-[var(--ink-2)]">{e.status}</span>}
            </div>
            {e.detail && <p className="mt-0.5 text-sm text-[var(--ink-2)]">{e.detail}</p>}
          </div>
          <span className="shrink-0 text-xs text-[var(--ink-3)]">{day(e.at)}</span>
          {more ? (open ? <ChevronDown size={16} className="mt-0.5 shrink-0 text-[var(--ink-3)]" /> : <ChevronRight size={16} className="mt-0.5 shrink-0 text-[var(--ink-3)]" />) : null}
        </button>
        {Object.keys(e.links || {}).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-3 text-xs">
            {Object.entries(e.links).map(([key, url]) => (
              <a key={key} href={url} target={url.startsWith('/') ? undefined : '_blank'} rel="noreferrer" className="inline-flex items-center gap-0.5 text-[var(--ink-2)] hover:underline">
                {LINK[key] || key} <ExternalLink size={11} /></a>))}
          </div>
        )}
        {open && (e.extra?.lines || []).length > 0 && (
          <ul className="mt-3 space-y-1.5 border-t border-[var(--line-2)] pt-3 text-sm">
            {e.extra.lines.map((l, i) => (
              <li key={i} className="flex flex-wrap gap-x-3">
                <span className="font-medium">{l.product}{l.variant ? ` (${l.variant})` : ''}</span>
                <span className="text-[var(--ink-2)]">{l.status}</span>
                {l.shipped ? <span className="text-green-700">shipped {day(l.shipped)}</span>
                  : l.expected && <span className="text-[var(--ink-3)]">expected {day(l.expected)}</span>}
                {l.tracking && <a href={l.tracking} target="_blank" rel="noreferrer" className="underline">tracking</a>}
              </li>))}
            {e.extra.payment && <li className="text-xs text-[var(--ink-3)]">Payment: {e.extra.payment}</li>}
          </ul>
        )}
        {open && (e.extra?.messages || []).length > 0 && (
          <ul className="mt-3 space-y-2 border-t border-[var(--line-2)] pt-3 text-sm">
            {e.extra.messages.map((m, i) => (
              <li key={i}>
                <span className={`text-xs font-semibold ${m.from === 'agent' ? 'text-[var(--ask)]' : 'text-[var(--ink-2)]'}`}>
                  {m.from === 'agent' ? (m.who || 'Agent') : 'Customer'}{m.channel && m.channel !== 'email' ? ` · ${m.channel}` : ''} · {day(m.at)}</span>
                <p className="text-[var(--ink-2)]">{m.text}</p>
              </li>))}
          </ul>
        )}
      </div>
    </li>
  );
};

const CustomersV2 = () => {
  const [params, setParams] = useSearchParams();
  const email = params.get('email') || '';
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);
  const [data, setData] = useState(null);
  const [only, setOnly] = useState('');
  useEffect(() => {
    if (q.trim().length < 2) { setHits(null); return undefined; }
    const t = setTimeout(() => axiosInstance.get(API, { params: { q } }).then(({ data: d }) => setHits(d.results || [])).catch(() => setHits([])), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!email) { setData(null); return; }
    setData(undefined); setOnly('');
    axiosInstance.get(API, { params: { email } }).then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, [email]);
  const pick = (em) => { setParams({ email: em }); setHits(null); setQ(''); };
  const events = useMemo(() => (data?.events || []).filter((e) => !only || e.kind === only), [data, only]);
  const c = data?.customer;
  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Customers</h1>
        <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">
          A customer's whole story in one place: orders, delays, tickets and who handled them, calls, claims, returns,
          refunds, disputes and reviews. Search by name, email or order number.
        </p>
        <div className="relative mt-5 max-w-xl">
          <label className="flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2">
            <Search size={16} className="text-[var(--ink-3)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nadia Dupont, nadia@… or #191242" className="flex-1 bg-transparent outline-none" autoFocus />
          </label>
          {hits && (
            <ul className="absolute z-10 mt-1 max-h-80 w-full overflow-auto rounded-md border border-[var(--line)] bg-[var(--surface)] text-sm shadow">
              {hits.map((h) => (
                <li key={h.email}><button type="button" onClick={() => pick(h.email)} className="w-full px-3 py-2 text-left hover:bg-[var(--paper)]">
                  <b>{h.name || h.email}</b> <span className="text-[var(--ink-3)]">{h.name ? h.email : ''} · {h.matched}</span></button></li>))}
              {!hits.length && <li className="px-3 py-2 text-[var(--ink-3)]">No customer found.</li>}
            </ul>
          )}
        </div>
        {data === undefined && <div className="mt-8 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {data === false && <p className="mt-8 text-sm text-red-700">Could not load this customer.</p>}
        {data && (
          <div className="mt-8 grid gap-8 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <aside className="space-y-4">
              <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
                <div className="text-lg font-semibold">{c.name || c.email}</div>
                {c.name && <div className="text-sm text-[var(--ink-3)]">{c.email}</div>}
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-xs text-[var(--ink-3)]">Orders</dt><dd className="num font-semibold">{c.orders}</dd></div>
                  <div><dt className="text-xs text-[var(--ink-3)]">Spent</dt><dd className="num font-semibold">{money(c.spent, c.currency)}</dd></div>
                  <div className="col-span-2"><dt className="text-xs text-[var(--ink-3)]">Customer since</dt><dd>{c.first_order ? day(c.first_order) : '–'}</dd></div>
                </dl>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setOnly('')} className={`rounded-full px-3 py-1 text-xs ${!only ? 'bg-[var(--ink)] text-white' : 'bg-[var(--line-2)] text-[var(--ink-2)]'}`}>All ({data.events.length})</button>
                {Object.entries(data.counts || {}).map(([k, n]) => (
                  <button key={k} type="button" onClick={() => setOnly(only === k ? '' : k)}
                    className={`rounded-full px-3 py-1 text-xs ${only === k ? 'bg-[var(--ink)] text-white' : 'bg-[var(--line-2)] text-[var(--ink-2)]'}`}>
                    {KIND[k]?.label || k} ({n})</button>))}
              </div>
              <p className="text-xs text-[var(--ink-3)]">Returns come from Returny, and from Backy for the ones before October.</p>
            </aside>
            <ol className="relative space-y-3 before:absolute before:bottom-2 before:left-[1.1rem] before:top-2 before:w-px before:bg-[var(--line)]">
              {events.map((e, i) => <Event key={`${e.kind}-${e.at}-${i}`} e={e} />)}
              {!events.length && <li className="text-sm text-[var(--ink-3)]">Nothing found for this customer.</li>}
            </ol>
          </div>
        )}
        {!email && !hits && (
          <div className="mt-10 flex items-center gap-3 text-sm text-[var(--ink-3)]"><Package size={18} /> Start by searching for a customer.</div>
        )}
      </div>
    </V2Shell>
  );
};

export default CustomersV2;
